import React from "react";
import PropTypes from "prop-types";
import { NavItem } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";

import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel
} from "react-accessible-accordion";

import {
  IoSpeedometer,
  IoBusiness,
  IoReader,
  IoTime,
  IoClipboard,
  IoBriefcase,
  IoFileTrayFull,
  IoTicket,
  IoReceipt,
  IoPersonCircle
} from "react-icons/io5";

import { validateAuthRoles } from "../../../helpers";

import MenuFooter from "./MenuFooter";

const MainMenu = ({ auth, isMobile, onClickMenu }) => (
  <ul className="sidebar sidebar-nav nav navbar-nav">
    <LinkContainer exact to="/app/dashboard">
      <NavItem onClick={onClickMenu}>
        <IoSpeedometer size={15} />
        <span>Dashboard</span>
      </NavItem>
    </LinkContainer>

    {/* TICKETS */}
    {auth.maxRole >= 4000 ? (
      <Accordion allowZeroExpanded>
        <AccordionItem>
          <AccordionItemHeading>
            <AccordionItemButton>
              <IoTicket size={15} />
              <span>Tickets</span>
            </AccordionItemButton>
          </AccordionItemHeading>

          <AccordionItemPanel>
            <LinkContainer to="/app/support-tickets">
              <NavItem onClick={onClickMenu}>Ticket List</NavItem>
            </LinkContainer>
            <LinkContainer to="/app/alt-customer-names">
              <NavItem onClick={onClickMenu}>Alt. Customer Names</NavItem>
            </LinkContainer>
            <LinkContainer to="/app/project-keywords">
              <NavItem onClick={onClickMenu}>Project Keywords</NavItem>
            </LinkContainer>
            <LinkContainer to="/app/support-resources">
              <NavItem onClick={onClickMenu}>Support Resources</NavItem>
            </LinkContainer>
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>
    ) : (
      <LinkContainer to="/app/support-tickets">
        <NavItem onClick={onClickMenu}>
          <IoTicket size={15} />
          <span>Tickets</span>
        </NavItem>
      </LinkContainer>
    )}

    {/* TIME MANAGEMENT */}
    <Accordion allowZeroExpanded>
      <AccordionItem>
        <AccordionItemHeading>
          <AccordionItemButton>
            <IoTime size={15} />
            <span>Time Management</span>
          </AccordionItemButton>
        </AccordionItemHeading>

        <AccordionItemPanel>
          <LinkContainer to="/app/calls/add" eventKey={1}>
            <NavItem onClick={onClickMenu}>New Call Entry</NavItem>
          </LinkContainer>

          <LinkContainer exact to="/app/calls" eventKey={3}>
            <NavItem onClick={onClickMenu}>Call Lookup</NavItem>
          </LinkContainer>

          {!isMobile && (
            <LinkContainer to="/app/timesheet" eventKey={2}>
              <NavItem onClick={onClickMenu}>Timesheet</NavItem>
            </LinkContainer>
          )}

          {!isMobile && (
            <LinkContainer to="/app/time-planner" eventKey={4}>
              <NavItem onClick={onClickMenu}>Time Planner</NavItem>
            </LinkContainer>
          )}

          {auth.user.roles.some(
            (role) => role.name === "Billing Admin" || role.name === "Admin"
          ) &&
            !isMobile && (
              <LinkContainer to="/app/bulk-migration" eventKey={5}>
                <NavItem onClick={onClickMenu}>Bulk Call Migration</NavItem>
              </LinkContainer>
            )}
        </AccordionItemPanel>
      </AccordionItem>
    </Accordion>

    {/* CUSTOMERS, CONTRACTS, PROJECTS */}
    {auth.user?.employment_status?.label !== "Consultant" && (
      <>
        <LinkContainer to="/app/customers">
          <NavItem onClick={onClickMenu}>
            <IoBusiness size={15} />
            <span>Customers</span>
          </NavItem>
        </LinkContainer>

        {/* CONTRACTS */}
        <Accordion allowZeroExpanded>
          <AccordionItem>
            <AccordionItemHeading>
              <AccordionItemButton>
                <IoReader size={15} />
                <span>Contracts</span>
              </AccordionItemButton>
            </AccordionItemHeading>

            <AccordionItemPanel>
              <LinkContainer to="/app/contracts">
                <NavItem onClick={onClickMenu}>Contract List</NavItem>
              </LinkContainer>
              <LinkContainer to="/app/it-health-score">
                <NavItem onClick={onClickMenu}>IT Health Score</NavItem>
              </LinkContainer>
            </AccordionItemPanel>
          </AccordionItem>
        </Accordion>

        <LinkContainer to="/app/projects">
          <NavItem onClick={onClickMenu}>
            <IoBriefcase size={15} />
            <span>Projects</span>
          </NavItem>
        </LinkContainer>
      </>
    )}

    {/* EXPENSES */}
    <LinkContainer to="/app/expenses">
      <NavItem onClick={onClickMenu}>
        <IoReceipt size={15} />
        <span>Expenses</span>
      </NavItem>
    </LinkContainer>

    {/* PTO */}
    <LinkContainer to="/app/pto">
      <NavItem onClick={onClickMenu}>
        <IoPersonCircle size={15} />
        <span>PTO Requests</span>
      </NavItem>
    </LinkContainer>

    {/* REVIEW */}
    {!isMobile &&
      auth.maxRole >= 4000 &&
      auth.user?.employment_status?.label !== "Consultant" && (
        <>
          <Accordion allowZeroExpanded>
            <AccordionItem>
              <AccordionItemHeading>
                <AccordionItemButton>
                  <IoClipboard size={15} />
                  <span>Review</span>
                </AccordionItemButton>
              </AccordionItemHeading>

              <AccordionItemPanel>
                {auth.maxRole >= 4000 && (
                  <LinkContainer to="/app/review/call" eventKey={5}>
                    <NavItem onClick={onClickMenu}>Call Approval</NavItem>
                  </LinkContainer>
                )}

                {auth.maxRole >= 8000 && (
                  <LinkContainer to="/app/review/finance" eventKey={5}>
                    <NavItem onClick={onClickMenu}>Finance Approval</NavItem>
                  </LinkContainer>
                )}

                {auth.user.roles.some(
                  (role) => role.name === "Billing Admin"
                ) && (
                  <LinkContainer to="/app/review/invoice" eventKey={6}>
                    <NavItem onClick={onClickMenu}>Invoice Approval</NavItem>
                  </LinkContainer>
                )}

                {auth.user.roles.some(
                  (role) => role.name === "Billing Admin"
                ) && (
                  <LinkContainer to="/app/review/billing" eventKey={6}>
                    <NavItem onClick={onClickMenu}>Billing Approval</NavItem>
                  </LinkContainer>
                )}

                <LinkContainer to="/app/review/expenses" eventKey={7}>
                  <NavItem onClick={onClickMenu}>Expense Approval</NavItem>
                </LinkContainer>

                {((auth.maxRole >= 5000 && auth.user && auth.user.manager) ||
                  validateAuthRoles(auth, "Admin, HR")) && (
                  <LinkContainer to="/app/review/pto">
                    <NavItem onClick={onClickMenu}>PTO Review</NavItem>
                  </LinkContainer>
                )}

                {auth.maxRole >= 5000 && (
                  <LinkContainer to="/app/review/project" eventKey={5}>
                    <NavItem onClick={onClickMenu}>Project Progress</NavItem>
                  </LinkContainer>
                )}
              </AccordionItemPanel>
            </AccordionItem>
          </Accordion>
        </>
      )}

    {/* REPORTS */}
    {!isMobile &&
      auth.maxRole >= 2000 &&
      auth.user?.employment_status?.label !== "Consultant" && (
        <LinkContainer to="/app/reports">
          <NavItem onClick={onClickMenu}>
            <IoFileTrayFull size={15} />
            <span>Reports</span>
          </NavItem>
        </LinkContainer>
      )}

    <MenuFooter />
  </ul>
);

MainMenu.propTypes = {
  auth: PropTypes.object,
  isMobile: PropTypes.bool,
  onClickMenu: PropTypes.func
};

export default MainMenu;
